import type { PlayerSummaryStats } from '@frontend/core/lib/domains/reporting/types'
import { ErrorBoundary } from '@frontend/react'
import {
  Card,
  GroupRadioToolbar,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  ToggleGroup,
  ToggleGroupItem
} from '@frontend/ui'
import { StatsTablePagination } from '../../../components/stats-table-pagination'
import { formatStatValue, getColumnUnitLabel } from '../../../lib/format-cell'
import { useReportingState } from '../../../lib/reporting-state'
import type { ColumnDef } from '../../../lib/sport-config'
import { getStatByAccessor } from '../../../lib/stat-utils'
import { useReportingUnitSystem } from '../../../lib/use-reporting-unit-system'
import type { DrillRow } from '../formatter'

// ---------------------------------------------------------------------------
// StatsCard
// ---------------------------------------------------------------------------

type TabGroup = {
  key: string
  label: string
  tabs: { key: string; label: string }[]
}

type StatsCardProps = {
  tabGroups: TabGroup[]
  activeGroupKey: string
  onGroupChange: (key: string) => void
  tabs: { key: string; label: string }[]
  activeTabKey: string
  onTabChange: (key: string) => void
  session: ReturnType<typeof useReportingState>['session']
  periodLabel: string
  columns: ColumnDef[]
  rows: DrillRow[]
  totals: PlayerSummaryStats | null
  totalDuration: string
  unitSystem: ReturnType<typeof useReportingUnitSystem>
  page: number
  pageCount: number
  onPageChange: (page: number) => void
}

export const StatsCard = ({
  tabGroups,
  activeGroupKey,
  onGroupChange,
  tabs,
  activeTabKey,
  onTabChange,
  session,
  periodLabel,
  columns,
  rows,
  totals,
  totalDuration,
  unitSystem,
  page,
  pageCount,
  onPageChange
}: StatsCardProps) => {
  return (
    <ErrorBoundary useUIFallback fallbackTitle='Failed to load stats table'>
      <Card className='gap-2 p-3 xl:max-h-full xl:min-h-0 xl:flex-1 xl:basis-0 xl:self-start xl:overflow-hidden'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          {/* Single-group sports (e.g. AMF / CFL) hide the radio toolbar —
              it adds visual noise when there's nothing to switch between. */}
          {tabGroups.length > 1 && (
            <GroupRadioToolbar
              groups={tabGroups}
              activeKey={activeGroupKey}
              onChange={onGroupChange}
            />
          )}
          <ToggleGroup
            type='single'
            variant='outline'
            value={activeTabKey}
            onValueChange={onTabChange}
          >
            {tabs.map((t) => (
              <ToggleGroupItem
                key={t.key}
                value={t.key}
                className='min-w-[100px] px-4'
              >
                {t.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className='border-border overflow-hidden rounded-lg border xl:flex-1 xl:overflow-auto'>
          <Table>
            <TableHeader>
              <TableRow className='border-border hover:bg-transparent'>
                <TableHead className='text-muted-foreground'>
                  {session?.type === 'training' ? 'Drill' : periodLabel}
                </TableHead>
                <TableHead className='text-muted-foreground'>
                  Duration
                </TableHead>
                {columns.map((col) => {
                  const unit = getColumnUnitLabel(col.unitType, unitSystem)
                  return (
                    <TableHead
                      key={col.accessor}
                      className='text-muted-foreground text-right'
                      title={col.fixedUnitNote}
                    >
                      {col.label}
                      {unit ? ` (${unit})` : ''}
                      {col.fixedUnitNote ? ' *' : ''}
                    </TableHead>
                  )
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} className='border-border'>
                  <TableCell className='text-foreground font-medium'>
                    {row.name}
                  </TableCell>
                  <TableCell className='text-muted-foreground tabular-nums'>
                    {row.duration}
                  </TableCell>
                  {columns.map((col) => (
                    <TableCell
                      key={col.accessor}
                      className='text-foreground text-right tabular-nums'
                    >
                      {formatStatValue(
                        getStatByAccessor(row.stats, col.accessor),
                        col.unitType,
                        unitSystem
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
            {totals && (
              <TableFooter>
                <TableRow className='bg-secondary/50'>
                  <TableCell className='text-foreground font-semibold'>
                    Totals
                  </TableCell>
                  <TableCell className='text-muted-foreground tabular-nums'>
                    {totalDuration}
                  </TableCell>
                  {columns.map((col) => (
                    <TableCell
                      key={col.accessor}
                      className='text-foreground text-right font-semibold tabular-nums'
                    >
                      {formatStatValue(
                        getStatByAccessor(totals, col.accessor),
                        col.unitType,
                        unitSystem
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </div>

        {pageCount > 1 && (
          <StatsTablePagination
            page={page}
            pageCount={pageCount}
            onPageChange={onPageChange}
          />
        )}
      </Card>
    </ErrorBoundary>
  )
}
